import { CollectionConfig } from "@rebasepro/types";
import digestSubscriptionsCollection from "./digest_subscriptions";
import talentsCollection from "./talents";

/**
 * What the newsletter actually sent, to whom, on which Tuesday.
 *
 * `digest_subscriptions.last_sent_at` is a watermark: it answers "where does
 * the next issue start from" and forgets everything else. This table answers
 * the two questions a mailing list is eventually asked instead — "did she get
 * last week's?" and "what was in it?" — and it answers them from rows written
 * *after* the mail server accepted the message, never when one was attempted.
 *
 * Not correspondence. These never become `email_threads` / `email_messages`:
 * the mail carries `Auto-Submitted: auto-generated`, and a digest is a
 * broadcast. The mailbox is for letters between people.
 *
 * `job_ids` is a list of ids and not a set of foreign keys, for two reasons.
 * It is a record of what an email said on a Tuesday and has to stay true after
 * a vacancy is closed or deleted; and — the rule this whole feature is built
 * on — it is not a link between a person and those vacancies. Nothing here
 * says she is a candidate for any of them. It says an email listed them,
 * because a filter she chose herself let them through. Only an application
 * connects a person to a vacancy in this schema.
 *
 * Table and RLS in drizzle/0057. Admin-only, and out of the drawer: it is a
 * log the panel reads, not a screen anybody works in.
 */
const digestSendsCollection: CollectionConfig = {
    name: "Envíos del boletín",
    singularName: "Envío del boletín",
    slug: "digest-sends",
    table: "digest_sends",
    securityRules: [
        {
            // Admin only. A candidate has every one of these in their own
            // inbox, which is a better copy than this one.
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        subscription_id: {
            name: "Suscripción",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => digestSubscriptionsCollection,
                localKey: "subscription_id"
            },
            validation: { required: true },
            admin: { readOnly: true }
        },
        talent_id: {
            name: "Candidato",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => talentsCollection,
                localKey: "talent_id"
            },
            description: "Quién lo recibió.",
            admin: { readOnly: true }
        },
        sent_at: {
            name: "Enviado",
            type: "date",
            description: "Cuándo lo aceptó el servidor de correo. No hay fila para un envío que no salió.",
            admin: { readOnly: true }
        },
        subject: {
            name: "Asunto",
            type: "string",
            description: "Tal y como salió: dice cuántas vacantes llevaba.",
            admin: { readOnly: true }
        },
        job_ids: {
            name: "Vacantes que incluía",
            type: "array",
            columnType: "text[]",
            of: { type: "string", name: "Vacante" },
            defaultValue: [],
            description: "Las vacantes que listaba ese correo. No es una relación con la persona: sólo dice qué se le mostró.",
            admin: { readOnly: true }
        },
        message_id: {
            name: "Message-ID",
            type: "string",
            description: "El identificador que devolvió el servidor, para rastrear una queja de entrega hasta esta fila.",
            admin: { readOnly: true }
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        }
    },
    admin: {
        group: "Personas",
        icon: "History",
        // Out of the drawer. An append-only log of machine-written rows is not
        // a place anybody does work, and a drawer entry beside "Boletín de
        // vacantes" would offer a choice between the subscription and the
        // receipts — the distinction this repo keeps making and nobody using it
        // makes. Still reachable by URL and from the relations that point here.
        hideFromNavigation: true,
        sort: ["sent_at", "desc"],
        listProperties: ["talent_id", "subject", "sent_at"],
        display: {
            title: "talent_id",
            subtitle: "subject",
            date: "sent_at"
        },
        previewProperties: ["talent_id", "sent_at"]
    }
};

export default digestSendsCollection;
